import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { throttle } from 'lodash';
import { useIdentify } from '@hungryroot/customer';
import { type CustomerProductRating } from '@hungryroot/rest';
import { type ProductPayload } from '@hungryroot/rest/api/v2/customers/[id]/product_ratings/post';
import { useDeleteCustomerProductRating } from '../../../hooks/useDeleteCustomerProductRating';
import { usePatchCustomerProductRatings } from '../../../hooks/usePatchCustomerProductRatings';
import { usePostCustomerProductRatings } from '../../../hooks/usePostCustomerProductRatings';
import {
  useSelfProductRating,
  invalidateQuery as invalidateSelfProductRatingQuery,
  getQueryKey as getSelfProductRatingQueryKey,
} from '../../../hooks/useSelfProductRating';
import { useOptimisticUpdateStatus } from '../useOptimisticUpdateStatus';
import { type RatableProductItem } from '../useRatableItems';

type UseThumbsProductRatingProps = {
  item: RatableProductItem;
  onError?: () => void;
  enableInitialFetch?: boolean;
};

type MutationContext =
  | { previousRating?: CustomerProductRating }
  | { previousRating?: boolean | null };

export function useThumbsProductRating(props: UseThumbsProductRatingProps) {
  const { item, onError, enableInitialFetch = false } = props;

  const {
    optimisticUpdateStatus,
    productRatingId,
    hasRated,
    localRating,
    postCustomerProductRatingsMutate,
    deleteCustomerProductRatingsMutate,
    patchCustomerProductRatingsMutate,
  } = useRatingMutations({
    item,
    onError,
    enableInitialFetch,
  });

  const { customerData } = useIdentify();

  const { data: selfProductRatingData } = useSelfProductRating({
    params: {
      id: productRatingId as number,
    },
    options: {
      enabled: hasRated && Number.isInteger(productRatingId),
    },
  });

  // Throttled API calls to prevent sending too many requests if the user
  // decides to rage click. This function handles which API call to make
  // based on the current state of the rating.
  const memoizedOnRatingApiCall = useMemo(
    () =>
      throttle((newValue: boolean | null) => {
        if (!customerData?.id) {
          return;
        }

        // There are no previous rating that we know of. Create a new one only
        // if the rating is a thumbs up or thumbs down (i.e., not `null`).
        if (!selfProductRatingData && newValue !== null) {
          postCustomerProductRatingsMutate({
            customerId: customerData.id,
            payload: {
              product: item.productId,
              liked: newValue,
              order: item.orderNumber,
            },
          });

          return;
        }

        // There's a previous rating and the user opted to remove it.
        if (selfProductRatingData && newValue === null && productRatingId) {
          deleteCustomerProductRatingsMutate({
            customer_id: customerData.id,
            id: productRatingId,
          });

          return;
        }

        // There's a previous rating and the user opted to change it (not deactivate).
        if (selfProductRatingData && newValue !== null && productRatingId) {
          patchCustomerProductRatingsMutate({
            customerId: customerData.id,
            payload: {
              id: productRatingId,
              liked: newValue,
              order: item.orderNumber,
              product: item.productId,
            },
          });

          return;
        }
      }, 1000),
    [
      productRatingId,
      item.orderNumber,
      item.productId,
      selfProductRatingData,
      customerData?.id,
      postCustomerProductRatingsMutate,
      deleteCustomerProductRatingsMutate,
      patchCustomerProductRatingsMutate,
    ]
  );

  return {
    rating: selfProductRatingData?.liked ?? localRating ?? null,
    onRating: memoizedOnRatingApiCall,
    optimisticUpdateStatus,
    productRatingId,
  };
}

type UseRatingMutationsProps = {
  item: RatableProductItem;
  onError?: () => void;
  enableInitialFetch?: boolean;
};

function useRatingMutations(props: UseRatingMutationsProps) {
  const { item, onError, enableInitialFetch = false } = props;
  // Keeps track of whether or not the user has rated this product at least
  // once. This is used to determine whether or not we should initally fetch
  // the latest rating from the server.
  const [hasRated, setHasRated] = useState(enableInitialFetch);

  const { optimisticUpdateStatus, setOptimisticUpdateStatus } =
    useOptimisticUpdateStatus();

  // Keeps track of the product rating ID for the current product. Unlike bundle
  // and pairing ratings, product ratings use an actual rating as the identifier.
  const [productRatingId, setProductRatingId] = useState<number | null>(null);

  // Temporary state to keep track of the rating before the rating is created
  // since we can't rely on the query key due to the lack of product rating ID.
  const [localRating, setLocalRating] = useState<boolean | null>(null);

  const queryKey = productRatingId
    ? getSelfProductRatingQueryKey({ id: productRatingId })
    : undefined;

  const queryClient = useQueryClient();
  const { customerData } = useIdentify();

  const onPostOrPatchMutate = async ({
    payload,
  }: {
    payload: { liked: boolean | null };
  }) => {
    setOptimisticUpdateStatus('pending');

    if (queryKey) {
      await queryClient.cancelQueries({ queryKey });

      let previousRating: CustomerProductRating | undefined =
        queryClient.getQueryData(queryKey);

      // Create an object that represents the product rating details as
      // if the API call was successful.
      const optimisticUpdate: CustomerProductRating = {
        // Placeholder identifiers. These will eventually be overridden with a
        // successful response from the server.
        id: item.productId,
        customer_id: customerData?.id ?? 0,

        ...previousRating,

        liked: payload.liked,
        order: item.orderNumber,
        product: item.productId,
      };

      queryClient.setQueryData(queryKey, optimisticUpdate);

      // Provide context for the mutation so we can revert the optimistic
      // update if anything goes wrong.
      return { previousRating };
    } else {
      // There isn't a query key so we haven't been able to create a product rating
      // yet. Fallback to a local state to keep track of the rating.
      let previousRating: boolean | null = localRating;

      setLocalRating(payload.liked);

      // Provide context for the mutation so we can revert the optimistic
      // update if anything goes wrong.
      return { previousRating };
    }
  };

  // `onError` handler when making the `POST` or `PATCH` call.
  const onPostOrPatchError = (
    _error: Error,
    _variables: {
      customerId: number;
      payload: ProductPayload;
    },
    context?: MutationContext
  ) => {
    onError?.();

    if (!productRatingId && typeof context?.previousRating === 'boolean') {
      setLocalRating(context?.previousRating);
    }

    // Remove the query data from the cache. If something went wrong here,
    // the data in the cache is likely not accurate and may cause us to attempt
    // to make the wrong type of request. Best to start off fresh.
    queryClient.removeQueries({ queryKey });
  };

  const onSettled = (data?: CustomerProductRating | void) => {
    setOptimisticUpdateStatus('settled');
    setHasRated(true);

    if (data) {
      // Pre-populate the cache with the successful response.
      const queryKey = getSelfProductRatingQueryKey({ id: data.id });
      queryClient.setQueryData(queryKey, data);

      // We have a product rating ID now, update the local rating state.
      setLocalRating(null);

      setProductRatingId(data.id);
    }

    // Invalidate the query so that the latest rating is fetched from the
    // server. The timeout is to prevent the GET from happening too soon
    // after to prevent stale data.
    if (productRatingId) {
      setTimeout(() => {
        invalidateSelfProductRatingQuery({
          id: productRatingId,
          queryClient,
        });
      }, 500);
    }
  };

  const { mutate: postCustomerProductRatingsMutate } =
    usePostCustomerProductRatings<MutationContext>({
      onMutate: onPostOrPatchMutate,
      onError: onPostOrPatchError,
      onSettled,
    });

  const { mutate: patchCustomerProductRatingsMutate } =
    usePatchCustomerProductRatings<MutationContext>({
      onMutate: onPostOrPatchMutate,
      onError: onPostOrPatchError,
      onSettled,
      // Remove the `onSuccess` handler from `usePatchCustomerProductRatings()`
      // since we're using the `onSettled` handler to handle things.
      onSuccess: undefined,
    });

  const { mutate: deleteCustomerProductRatingsMutate } =
    useDeleteCustomerProductRating({
      onMutate: async () => {
        setOptimisticUpdateStatus('pending');

        if (queryKey) {
          await queryClient.cancelQueries({ queryKey });

          const previousRating: CustomerProductRating | undefined =
            queryClient.getQueryData(queryKey);

          queryClient.setQueryData(queryKey, null);

          return { previousRating };
        }
      },

      onError: (_error, _variables, context) => {
        onError?.();

        if (context && queryKey) {
          queryClient.setQueryData(queryKey, context.previousRating);
        }
      },

      onSettled,
    });

  return {
    optimisticUpdateStatus,
    productRatingId,
    hasRated,
    localRating,
    postCustomerProductRatingsMutate,
    deleteCustomerProductRatingsMutate,
    patchCustomerProductRatingsMutate,
  };
}
